import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDoctors,
  fetchDoctorStats,
  validateDoctor,
  fetchDoctorById,
} from '../redux/thunks/adminThunks';
import { setDoctorStatusLocal } from '../redux/slices/adminSlice';
import {
  selectDoctors,
  selectDoctorsCount,
  selectDoctorStats,
  selectDoctorsStatus,
  selectDoctorsError,
  makeSelectDoctorById,
} from '../redux/selectors/adminSelectors';

/* =========================================================
   HELPERS IMAGES (à utiliser dans les composants)
========================================================= */

// Base URL du ms-authDoctor pour servir les images
export const API_UPLOAD_BASE =
  import.meta.env.VITE_DOCTOR_UPLOADS_URL ||
  `${import.meta.env.VITE_GATEWAY_URL || window.location.origin}/uploads`;

/**
 * Transforme un chemin BD (Windows absolu ou relatif) en URL HTTP utilisable.
 * Ex:
 *  - "F:\MedilinkBack\ms-authDoctor\uploads\doctors\1776.jpeg"
 *    -> configured doctor upload base
 *  - "doctors/1776.jpeg"
 *    -> configured doctor upload base
 */
export function buildDoctorImageUrl(src) {
  if (!src) return null;

  // 1) Chemin Windows absolu (comme stocké en BDD)
  if (src.startsWith('F:\\') || src.startsWith('C:\\')) {
    const parts = src.split('\\'); // ['F:', 'MedilinkBack', 'ms-authDoctor', 'uploads', 'doctors', '1776.jpeg']
    const filename = parts.pop();  // '1776.jpeg'
    const folder = parts.pop();    // 'doctors'
    return `${API_UPLOAD_BASE}/${folder}/${filename}`;
  }

  // 2) Chemin relatif type "doctors/1776.jpeg"
  if (!src.startsWith('http') && !src.startsWith('/uploads')) {
    return `${API_UPLOAD_BASE}/${src.replace(/^\\+|^\/+/, '')}`;
  }

  // 3) Cas déjà "/uploads/..." → préfixe host:port
  if (src.startsWith('/uploads')) {
    return `${API_UPLOAD_BASE.replace(/\/+$/, "")}${src}`;
  }

  // 4) URL http absolue → laisser tel quel
  return src;
}

/* =========================================================
   HOOK LISTE DES DOCTEURS
========================================================= */

/**
 * Hook liste/collection des docteurs
 * options:
 *  - status?: 'pending' | 'confirmed' | 'rejected' (filtrage côté API)
 *  - autoFetch?: boolean (true par défaut)
 */
export function useDoctors(options = {}) {
  const { status, autoFetch = true } = options;

  const dispatch = useDispatch();

  const items = useSelector(selectDoctors);
  const total = useSelector(selectDoctorsCount);
  const stats = useSelector(selectDoctorStats);
  const sliceStatus = useSelector(selectDoctorsStatus);
  const error = useSelector(selectDoctorsError);

  const isLoading = sliceStatus === 'loading';

  // Charger la liste au montage (et quand le filtre de status change)
  useEffect(() => {
    if (!autoFetch) return;
    dispatch(fetchDoctors({ status }));
  }, [dispatch, status, autoFetch]);

  // Charger les stats à la demande
  const loadStats = useCallback(() => {
    return dispatch(fetchDoctorStats());
  }, [dispatch]);

  // Rafraîchir (en gardant le filtre courant)
  const refetch = useCallback(
    (next = {}) => {
      const finalStatus = next.status !== undefined ? next.status : status;
      return dispatch(fetchDoctors({ status: finalStatus }));
    },
    [dispatch, status]
  );

  // Confirm (optimistic by default + rollback si erreur)
  const confirmDoctor = useCallback(
    async (id, { optimistic = true } = {}) => {
      const prev = items.find((d) => String(d.id) === String(id))?.status;
      if (optimistic) {
        dispatch(setDoctorStatusLocal({ id, status: 'confirmed' }));
      }
      try {
        await dispatch(validateDoctor({ id, status: 'confirmed' })).unwrap();
      } catch (e) {
        if (optimistic && prev) {
          dispatch(setDoctorStatusLocal({ id, status: prev }));
        }
        throw e;
      }
    },
    [dispatch, items]
  );

  // Reject (rejectionReason requis par l’API) — optimistic + rollback si erreur
  const rejectDoctor = useCallback(
    async (id, rejectionReason, { optimistic = true } = {}) => {
      if (!rejectionReason) {
        throw new Error('rejectionReason est requis pour rejeter un médecin');
      }
      const prev = items.find((d) => String(d.id) === String(id))?.status;
      if (optimistic) {
        dispatch(setDoctorStatusLocal({ id, status: 'rejected' }));
      }
      try {
        await dispatch(
          validateDoctor({ id, status: 'rejected', rejectionReason })
        ).unwrap();
      } catch (e) {
        if (optimistic && prev) {
          dispatch(setDoctorStatusLocal({ id, status: prev }));
        }
        throw e;
      }
    },
    [dispatch, items]
  );

  return {
    items,
    total,
    stats,
    isLoading,
    error,

    // actions
    refetch,
    loadStats,
    confirmDoctor,
    rejectDoctor,
  };
}

/* =========================================================
   HOOK DÉTAIL DOCTEUR
========================================================= */

/**
 * Hook détail d’un docteur
 * - id: identifiant du médecin (string/UUID)
 * - options:
 *    - autoFetch?: boolean (true par défaut)
 */
export function useDoctor(id, options = {}) {
  const { autoFetch = true } = options;
  const dispatch = useDispatch();

  const selectById = useMemo(() => makeSelectDoctorById(id), [id]);
  const doctor = useSelector(selectById);

  const sliceStatus = useSelector(selectDoctorsStatus);
  const error = useSelector(selectDoctorsError);
  const isLoading = sliceStatus === 'loading';

  useEffect(() => {
    if (!autoFetch || id == null) return;
    dispatch(fetchDoctorById(id));
  }, [dispatch, id, autoFetch]);

  const refetch = useCallback(() => {
    if (id == null) return Promise.resolve();
    return dispatch(fetchDoctorById(id));
  }, [dispatch, id]);

  const confirm = useCallback(
    async ({ optimistic = true } = {}) => {
      if (!id) return;
      const prev = doctor?.status;
      if (optimistic) {
        dispatch(setDoctorStatusLocal({ id, status: 'confirmed' }));
      }
      try {
        await dispatch(validateDoctor({ id, status: 'confirmed' })).unwrap();
      } catch (e) {
        if (optimistic && prev) {
          dispatch(setDoctorStatusLocal({ id, status: prev }));
        }
        throw e;
      }
    },
    [dispatch, id, doctor?.status]
  );

  const reject = useCallback(
    async (rejectionReason, { optimistic = true } = {}) => {
      if (!id) return;
      if (!rejectionReason) {
        throw new Error('rejectionReason est requis pour rejeter un médecin');
      }
      const prev = doctor?.status;
      if (optimistic) {
        dispatch(setDoctorStatusLocal({ id, status: 'rejected' }));
      }
      try {
        await dispatch(
          validateDoctor({ id, status: 'rejected', rejectionReason })
        ).unwrap();
      } catch (e) {
        if (optimistic && prev) {
          dispatch(setDoctorStatusLocal({ id, status: prev }));
        }
        throw e;
      }
    },
    [dispatch, id, doctor?.status]
  );

  return {
    doctor,
    isLoading,
    error,

    // actions
    refetch,
    confirm,
    reject,
  };
}